import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import Modal from './Modal';
import Button from './ui/Button';
import './TemplateUpdateModal.css';
import { translateError } from '../utils/errorMessages';

// =============================================================================
// TYPES
// =============================================================================

interface AffectedEvent {
  _id: string;
  name: string;
  startTime: string;
  hasManualChanges: boolean;
}

interface PreviewResponse {
  changedFields: string[];
  scheduleChanged: boolean;
  affectedEvents: AffectedEvent[];
  eventsWithManualChanges: number;
  totalAffectedEvents: number;
}

export type UpdateOption = 'all' | 'selected' | 'none';

interface TemplateUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (option: UpdateOption, selectedEventIds?: string[]) => void;
  templateId: string;
  changes: Record<string, any>;
  scheduleChanged: boolean;
  loading?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

const TemplateUpdateModal: React.FC<TemplateUpdateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  templateId,
  changes,
  scheduleChanged,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [updateOption, setUpdateOption] = useState<UpdateOption>('all');
  const [selectedEvents, setSelectedEvents] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch preview when modal opens
  const fetchPreview = useCallback(async () => {
    if (!isOpen || !templateId) return;

    setPreviewLoading(true);
    setError(null);

    try {
      const response = await apiClient.post<PreviewResponse>(
        `/api/event-templates/${templateId}/preview-changes`,
        { changes }
      );
      setPreview(response.data);

      // By default, select all events
      const allEventIds = new Set(response.data.affectedEvents.map((e) => e._id));
      setSelectedEvents(allEventIds);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setPreviewLoading(false);
    }
  }, [isOpen, templateId, changes]);

  useEffect(() => {
    if (isOpen) {
      fetchPreview();
      setUpdateOption('all');
    }
  }, [isOpen, fetchPreview]);

  const handleEventToggle = (eventId: string) => {
    setSelectedEvents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (preview) {
      setSelectedEvents(new Set(preview.affectedEvents.map((e) => e._id)));
    }
  };

  const handleDeselectAll = () => {
    setSelectedEvents(new Set());
  };

  const handleConfirm = () => {
    if (updateOption === 'selected') {
      onConfirm(updateOption, Array.from(selectedEvents));
    } else {
      onConfirm(updateOption);
    }
  };

  const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('templateUpdate.title', 'Update Template')}
      size="large"
    >
      <div className="template-update-modal">
        {previewLoading ? (
          <div className="template-update-modal__loading">
            {t('common.loading')}
          </div>
        ) : error ? (
          <div className="template-update-modal__error">
            {t('common.error')}: {error}
          </div>
        ) : (
          <>
            {/* Schedule change warning */}
            {scheduleChanged && (
              <div className="template-update-modal__warning">
                <strong>{t('templateUpdate.scheduleWarningTitle', 'Schedule Changes Detected')}</strong>
                <p>
                  {t(
                    'templateUpdate.scheduleWarning',
                    'You have changed schedule-related fields. Existing events will be regenerated based on the new schedule. Events with manual customizations will be preserved unless explicitly selected.'
                  )}
                </p>
              </div>
            )}

            {/* Update options */}
            <div className="template-update-modal__options">
              <label className="template-update-modal__option">
                <input
                  type="radio"
                  name="updateOption"
                  value="all"
                  checked={updateOption === 'all'}
                  onChange={() => setUpdateOption('all')}
                />
                <div className="template-update-modal__option-content">
                  <span className="template-update-modal__option-title">
                    {t('templateUpdate.optionAll', 'Update all future events')}
                  </span>
                  <span className="template-update-modal__option-desc">
                    {t(
                      'templateUpdate.optionAllDesc',
                      'Apply changes to all {{count}} future events from this template',
                      { count: preview?.totalAffectedEvents || 0 }
                    )}
                  </span>
                </div>
              </label>

              <label className="template-update-modal__option">
                <input
                  type="radio"
                  name="updateOption"
                  value="selected"
                  checked={updateOption === 'selected'}
                  onChange={() => setUpdateOption('selected')}
                />
                <div className="template-update-modal__option-content">
                  <span className="template-update-modal__option-title">
                    {t('templateUpdate.optionSelected', 'Update only selected events')}
                  </span>
                  <span className="template-update-modal__option-desc">
                    {t(
                      'templateUpdate.optionSelectedDesc',
                      'Choose which events should receive the updates'
                    )}
                  </span>
                </div>
              </label>

              <label className="template-update-modal__option">
                <input
                  type="radio"
                  name="updateOption"
                  value="none"
                  checked={updateOption === 'none'}
                  onChange={() => setUpdateOption('none')}
                />
                <div className="template-update-modal__option-content">
                  <span className="template-update-modal__option-title">
                    {t('templateUpdate.optionNone', 'Only affect new events')}
                  </span>
                  <span className="template-update-modal__option-desc">
                    {t(
                      'templateUpdate.optionNoneDesc',
                      'Keep existing events unchanged, only apply to newly generated events'
                    )}
                  </span>
                </div>
              </label>
            </div>

            {/* Event selection list (only show when 'selected' option is chosen) */}
            {updateOption === 'selected' && preview && preview.affectedEvents.length > 0 && (
              <div className="template-update-modal__events">
                <div className="template-update-modal__events-header">
                  <span>
                    {t('templateUpdate.selectEvents', 'Select events to update')} ({selectedEvents.size}/{preview.affectedEvents.length})
                  </span>
                  <div className="template-update-modal__events-actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="template-update-modal__select-btn"
                      onClick={handleSelectAll}
                    >
                      {t('common.selectAll', 'Select all')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="template-update-modal__select-btn"
                      onClick={handleDeselectAll}
                    >
                      {t('common.deselectAll', 'Deselect all')}
                    </Button>
                  </div>
                </div>

                <ul className="template-update-modal__events-list">
                  {preview.affectedEvents.map((event) => (
                    <li
                      key={event._id}
                      className={`template-update-modal__event ${
                        event.hasManualChanges ? 'has-changes' : ''
                      }`}
                    >
                      <label className="template-update-modal__event-label">
                        <input
                          type="checkbox"
                          checked={selectedEvents.has(event._id)}
                          onChange={() => handleEventToggle(event._id)}
                        />
                        <div className="template-update-modal__event-info">
                          <span className="template-update-modal__event-date">
                            {formatDateTime(event.startTime)}
                          </span>
                          {event.hasManualChanges && (
                            <span
                              className="template-update-modal__event-badge"
                              title={t('templateUpdate.hasManualChanges', 'Has manual customizations')}
                            >
                              *
                            </span>
                          )}
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>

                {preview.eventsWithManualChanges > 0 && (
                  <div className="template-update-modal__events-note">
                    <span className="template-update-modal__event-badge">*</span>
                    {t(
                      'templateUpdate.manualChangesNote',
                      'Events marked with * have manual customizations'
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="template-update-modal__actions">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={loading}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirm}
                loading={loading}
                disabled={updateOption === 'selected' && selectedEvents.size === 0}
              >
                {t('templateUpdate.confirm', 'Update Template')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default TemplateUpdateModal;
